import { clsx } from 'clsx';
import { groupBy } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { VendorAccountHealth } from 'src/components/VendorAccountHealth';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { Content } from 'src/contexts/ContentContext';
import { PosDropdown, PosDropdownItem } from 'src/core/POS/PosDropdown';
import { vars } from 'src/core/themes';
import { Button, SimpleTable, Stack, Tooltip } from 'src/core/ui';
import { CopyToClipboard } from 'src/core/ui/CopyToClipboard';
import { useOffices } from 'src/hooks/api/useOffices';
import { usePurchaseActions } from 'src/hooks/usePurchaseActions';
import {
  UseGetUserInfo,
  UseGetUserInfos,
  useGetUserInfos,
} from 'src/hooks/userGetUserInfo';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { LaunchUnspoilTickets } from 'src/modals/SaleDetails/actions/LaunchUnspoilTickets';
import { ListingLinksDetail } from 'src/modals/SaleDetails/components/ListingLinksDetail';
import { GenericDisclosuresIcon, WarningOutlineIcon } from 'src/svgs/Viagogo';
import { IconsFill, MoreIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  Feature,
  SaleDetails,
  SaleTicketGroupInfo,
} from 'src/WebApiController';
import { Ticket } from 'src/WebApiController';

import * as tableStyles from '../../../SaleDetails/SaleDetailsTable.css';
import * as styles from './SaleTickets.css';

interface AllocatedListingsProps {
  sale: SaleDetails;
}

type ListingActionOption = {
  key: string;
  action: React.ReactNode;
};

type ListingTicketGroupType = {
  listingId: number;
  ticketGroups: SaleTicketGroupInfo[];
  tickets: Ticket[];
  hasSpoiledTickets?: boolean;
  hasWastedTickets?: boolean;
};

const toListingTicketGroupRank = (item: ListingTicketGroupType) => {
  if (!item.hasSpoiledTickets && !item.hasWastedTickets) return 0;
  if (item.hasWastedTickets) return 1;
  return 2;
};

export const AllocatedListings = ({ sale }: AllocatedListingsProps) => {
  const { ticketGroupInfos, tickets } = sale;

  const listingTicketGroups = useMemo<ListingTicketGroupType[]>(() => {
    const ticketsByListingId = groupBy(tickets, (t) => t.listingId);
    return Object.entries(ticketsByListingId)
      .map(
        ([listingId, listingTickets]): ListingTicketGroupType | undefined => {
          if (listingTickets == null) {
            return undefined;
          }
          const ticketGroupIds = listingTickets.map((t) => t.tgId);
          const ticketGroups = ticketGroupInfos.filter((tg) =>
            ticketGroupIds.includes(tg.ticketGroupId)
          );
          return {
            listingId: parseInt(listingId),
            ticketGroups,
            tickets: listingTickets,
            hasSpoiledTickets: Boolean(
              listingTickets.find((t) => t.spoiledSaleId != null)
            ),
            hasWastedTickets: Boolean(listingTickets.find((t) => t.isWasted)),
          };
        }
      )
      .filter(
        (l: ListingTicketGroupType | undefined): l is ListingTicketGroupType =>
          l != null
      )
      .toSorted((a, b) => {
        return toListingTicketGroupRank(a) - toListingTicketGroupRank(b);
      });
  }, [ticketGroupInfos, tickets]);

  const canRender = sale.originatedFromListingId;

  const buyerUserIds = useMemo<string[]>(() => {
    if (!canRender) {
      return []; // Don't fetch if it can't render
    }
    return ticketGroupInfos
      .filter((tkgInfo) => !!tkgInfo.buyerUserId)
      .map((tkgInfo) => tkgInfo.buyerUserId) as string[];
  }, [canRender, ticketGroupInfos]);

  const usersInfoQuery = useGetUserInfos(buyerUserIds);
  const usersMap: UseGetUserInfos = useMemo(
    () => usersInfoQuery.data || {},
    [usersInfoQuery.data]
  );

  if (!canRender) {
    return null;
  }

  return (
    <>
      {listingTicketGroups.map((listingTicketGroup, index) => {
        return (
          <ListingTicketGroup
            key={listingTicketGroup.listingId}
            index={index}
            listingId={listingTicketGroup.listingId}
            sale={sale}
            ticketGroups={listingTicketGroup.ticketGroups}
            tickets={listingTicketGroup.tickets}
            hasSpoiledTickets={listingTicketGroup.hasSpoiledTickets}
            hasWastedTickets={listingTicketGroup.hasWastedTickets}
            usersMap={usersMap}
          />
        );
      })}
    </>
  );
};

export const ListingTicketGroup = ({
  index,
  listingId,
  sale,
  ticketGroups,
  usersMap,
  tickets,
  hasWastedTickets,
  hasSpoiledTickets,
}: ListingTicketGroupType & {
  index: number;
  sale: SaleDetails;
  usersMap: Partial<Record<string, UseGetUserInfo>>;
}) => {
  const { isOfficeEnabled } = useOffices();
  const { setActivePosEntity } = useActivePosEntityContext<SaleDetails>();
  const hasSurfaceBadVendAccCredentials = useUserHasFeature(
    Feature.SurfaceBadVendAccCredentials
  );

  const headerVendorAccount = useMemo(
    () =>
      hasSurfaceBadVendAccCredentials
        ? (ticketGroups.find(
            (tg) => tg.vendorAccount?.vendorAccountCredentialsIssueType != null
          )?.vendorAccount ?? ticketGroups[0]?.vendorAccount)
        : undefined,
    [hasSurfaceBadVendAccCredentials, ticketGroups]
  );

  const ticketIdsToUnspoil = useMemo(() => {
    return (
      tickets
        .filter((t) => t.listingId === listingId && t.spoiledSaleId != null)
        .map((t) => t.id) ?? []
    );
  }, [listingId, tickets]);

  const listingOperations = useMemo((): ListingActionOption[] => {
    const actions: ListingActionOption[] = [];
    if (hasSpoiledTickets) {
      actions.push({
        key: 'UnspoilTickets',
        action: (
          <LaunchUnspoilTickets
            saleId={sale.id}
            marketplaceSaleId={sale.idOnMkp}
            ticketIdsToUnspoil={ticketIdsToUnspoil}
            variant={'text'}
          />
        ),
      });
    }
    return actions;
  }, [hasSpoiledTickets, sale.id, sale.idOnMkp, ticketIdsToUnspoil]);

  const listingLabel = useMemo(() => {
    if (hasWastedTickets) {
      return (
        <span className={styles.errorLabel}>
          <Content id={ContentId.Wasted} />
        </span>
      );
    }
    if (hasSpoiledTickets) {
      return (
        <span className={styles.errorLabel}>
          <Content id={ContentId.Spoiled} />
        </span>
      );
    }
    return (
      <span className={styles.successLabel}>
        <Content id={ContentId.Delivery} />
      </span>
    );
  }, [hasSpoiledTickets, hasWastedTickets]);

  const getBuyerName = useCallback(
    (buyerUserId: string | null) => {
      if (!buyerUserId) {
        return undefined;
      }
      return usersMap[buyerUserId]?.name;
    },
    [usersMap]
  );

  return (
    <div>
      <Stack alignItems="center" justifyContent="spaceBetween">
        <Stack gap="l" alignItems="center">
          <span className={styles.tableTitle}>
            {index === 0 && <Content id={ContentId.AllocatedListing} />}
          </span>
          <ListingLinksDetail listingId={listingId} showTitle={false} />
          {listingLabel}
          {index === 0 &&
            isOfficeEnabled &&
            sale.showCrossOfficeAllocationWarning && (
              <Tooltip
                triggerContent={
                  <Stack direction="row" gap="s">
                    <WarningOutlineIcon
                      size={vars.iconSize.s}
                      fill={IconsFill.textNegative}
                    />
                    <span
                      style={{
                        color: vars.color.textNegative,
                        fontSize: vars.typography.fontSize.xs,
                      }}
                    >
                      <Content id={ContentId.AutoFulfillmentDisabled} />
                    </span>
                  </Stack>
                }
              >
                <Content id={ContentId.CrossStorefrontAllocationWarning} />
              </Tooltip>
            )}
        </Stack>
        <Stack direction="row" alignItems="center" gap="m">
          {hasSurfaceBadVendAccCredentials && headerVendorAccount && (
            <VendorAccountHealth
              mode="full"
              vendorAccount={headerVendorAccount}
              onVendorAccountSaved={() =>
                setActivePosEntity(sale.id, sale.idOnMkp, true)
              }
            />
          )}
          {listingOperations.length > 0 && (
            <PosDropdown
              trigger={
                <MoreIcon
                  withHoverEffect
                  fill={IconsFill.textBrand}
                  align="middle"
                />
              }
              align="end"
            >
              {listingOperations
                .toSorted((a, b) => a.key.localeCompare(b.key))
                .map((a) => (
                  <PosDropdownItem key={a.key}>{a.action}</PosDropdownItem>
                ))}
            </PosDropdown>
          )}
        </Stack>
      </Stack>
      <ListingTicketGroupFormData
        getBuyerName={getBuyerName}
        ticketGroups={ticketGroups}
      />
    </div>
  );
};

export const AllocatedListing = ({ sale }: { sale: SaleDetails }) => {
  const { isOfficeEnabled } = useOffices();
  const { setActivePosEntity } = useActivePosEntityContext<SaleDetails>();
  const { ticketGroupInfos } = sale;
  const hasSpoilTicketsInSaleFeature = useUserHasFeature(
    Feature.SpoilTicketsInSale
  );
  const hasSurfaceBadVendAccCredentials = useUserHasFeature(
    Feature.SurfaceBadVendAccCredentials
  );
  const canRender = sale.originatedFromListingId;

  const headerVendorAccount = useMemo(
    () =>
      hasSurfaceBadVendAccCredentials
        ? (ticketGroupInfos.find(
            (tg) => tg.vendorAccount?.vendorAccountCredentialsIssueType != null
          )?.vendorAccount ?? ticketGroupInfos[0]?.vendorAccount)
        : undefined,
    [hasSurfaceBadVendAccCredentials, ticketGroupInfos]
  );

  const buyerUserIds = useMemo<string[]>(() => {
    if (!canRender) {
      return []; // Don't fetch if it can't render
    }
    return ticketGroupInfos
      .filter((tkgInfo) => !!tkgInfo.buyerUserId)
      .map((tkgInfo) => tkgInfo.buyerUserId) as string[];
  }, [canRender, ticketGroupInfos]);

  const usersInfoQuery = useGetUserInfos(buyerUserIds);
  const usersMap: UseGetUserInfos = useMemo(
    () => usersInfoQuery.data || {},
    [usersInfoQuery.data]
  );

  const ticketIdsToUnspoil = useMemo(() => {
    return (
      sale.tickets
        .filter(
          (t) => t.listingId === sale.listingId && t.spoiledSaleId != null
        )
        .map((t) => t.id) ?? []
    );
  }, [sale.listingId, sale.tickets]);

  const canUnspoilTickets = useMemo(() => {
    if (!hasSpoilTicketsInSaleFeature) {
      return false;
    }
    return ticketIdsToUnspoil.length > 0;
  }, [hasSpoilTicketsInSaleFeature, ticketIdsToUnspoil.length]);

  const listingOperations = useMemo((): ListingActionOption[] => {
    const actions: ListingActionOption[] = [];
    if (canUnspoilTickets) {
      actions.push({
        key: 'UnspoilTickets',
        action: (
          <LaunchUnspoilTickets
            saleId={sale.id}
            marketplaceSaleId={sale.idOnMkp}
            ticketIdsToUnspoil={ticketIdsToUnspoil}
            variant={'text'}
          />
        ),
      });
    }
    return actions;
  }, [canUnspoilTickets, sale.id, sale.idOnMkp, ticketIdsToUnspoil]);

  const getBuyerName = useCallback(
    (buyerUserId: string | null) => {
      if (!buyerUserId) {
        return undefined;
      }
      return usersMap[buyerUserId]?.name;
    },
    [usersMap]
  );

  if (!canRender) {
    return null;
  }

  return (
    <div>
      <Stack alignItems="center" justifyContent="spaceBetween">
        <Stack gap="xxl" alignItems="center">
          <span className={styles.tableTitle}>
            <Content id={ContentId.AllocatedListing} />
          </span>
          <ListingLinksDetail listingId={sale.listingId} showTitle={false} />
          {isOfficeEnabled && sale.showCrossOfficeAllocationWarning && (
            <Tooltip
              triggerContent={
                <Stack direction="row" gap="s">
                  <WarningOutlineIcon
                    size={vars.iconSize.s}
                    fill={IconsFill.textNegative}
                  />
                  <span
                    style={{
                      color: vars.color.textNegative,
                      fontSize: vars.typography.fontSize.xs,
                    }}
                  >
                    <Content id={ContentId.AutoFulfillmentDisabled} />
                  </span>
                </Stack>
              }
            >
              <Content id={ContentId.CrossStorefrontAllocationWarning} />
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" gap="m">
          {hasSurfaceBadVendAccCredentials && headerVendorAccount && (
            <VendorAccountHealth
              mode="full"
              vendorAccount={headerVendorAccount}
              onVendorAccountSaved={() =>
                setActivePosEntity(sale.id, sale.idOnMkp, true)
              }
            />
          )}
          {listingOperations.length > 0 && (
            <PosDropdown
              trigger={
                <MoreIcon
                  withHoverEffect
                  fill={IconsFill.textBrand}
                  align="middle"
                />
              }
              align="end"
            >
              {listingOperations
                .toSorted((a, b) => a.key.localeCompare(b.key))
                .map((a) => (
                  <PosDropdownItem key={a.key}>{a.action}</PosDropdownItem>
                ))}
            </PosDropdown>
          )}
        </Stack>
      </Stack>

      <ListingTicketGroupFormData
        getBuyerName={getBuyerName}
        ticketGroups={ticketGroupInfos}
      />
    </div>
  );
};

const ListingTicketGroupFormData = ({
  ticketGroups,
  getBuyerName,
}: {
  ticketGroups: SaleTicketGroupInfo[];
  getBuyerName: (buyerUserId: string | null) => string | undefined;
}) => {
  const { openPurchaseModal } = usePurchaseActions();
  const hasSurfaceBadVendAccCredentials = useUserHasFeature(
    Feature.SurfaceBadVendAccCredentials
  );
  return (
    <div className={tableStyles.tableWrapper}>
      <SimpleTable.Table className={tableStyles.table}>
        <SimpleTable.Thead showBottomBorder={false}>
          <SimpleTable.Tr className={tableStyles.tableRow}>
            <SimpleTable.Th
              className={clsx(tableStyles.tableHeader, tableStyles.sectionCell)}
            >
              <Content id={ContentId.Section} />
            </SimpleTable.Th>
            <SimpleTable.Th
              className={clsx(tableStyles.tableHeader, tableStyles.smallCell)}
            >
              <Content id={ContentId.Row} />
            </SimpleTable.Th>
            <SimpleTable.Th
              className={clsx(tableStyles.tableHeader, tableStyles.smallCell)}
            >
              <Content id={ContentId.Seats} />
            </SimpleTable.Th>
            <SimpleTable.Th
              className={clsx(tableStyles.tableHeader, tableStyles.smallCell)}
            >
              <Content id={ContentId.QuantityAbbreviated} />
            </SimpleTable.Th>
            <SimpleTable.Th className={tableStyles.tableHeader}>
              <Content id={ContentId.Purchase} />
            </SimpleTable.Th>
            <SimpleTable.Th className={tableStyles.tableHeader}>
              <Content id={ContentId.PurchasedBy} />
            </SimpleTable.Th>
            <SimpleTable.Th className={tableStyles.tableHeader}>
              <Content id={ContentId.Vendor} />
            </SimpleTable.Th>
            <SimpleTable.Th
              className={clsx(tableStyles.tableHeader, tableStyles.smallCell)}
            >
              <Content id={ContentId.Notes} />
            </SimpleTable.Th>
          </SimpleTable.Tr>
        </SimpleTable.Thead>
        <SimpleTable.Tbody>
          {ticketGroups.map((tkInfo, index) => {
            const { seating } = tkInfo;
            return (
              <SimpleTable.Tr
                className={tableStyles.tableRow}
                key={tkInfo.ticketGroupId}
              >
                <SimpleTable.Td
                  className={clsx(
                    tableStyles.tableCell,
                    tableStyles.sectionCell
                  )}
                >
                  {seating.section}
                </SimpleTable.Td>
                <SimpleTable.Td
                  className={clsx(tableStyles.tableCell, tableStyles.smallCell)}
                >
                  {seating.row}
                </SimpleTable.Td>
                <SimpleTable.Td
                  className={clsx(tableStyles.tableCell, tableStyles.smallCell)}
                >
                  {seating.seatFr} - {seating.seatTo}
                </SimpleTable.Td>
                <SimpleTable.Td
                  className={clsx(tableStyles.tableCell, tableStyles.smallCell)}
                >
                  {tkInfo.ticketCount}
                </SimpleTable.Td>
                <SimpleTable.Td className={tableStyles.tableCell}>
                  <Stack>
                    <Button
                      variant="link"
                      onClick={(e) => {
                        openPurchaseModal(tkInfo.purchaseOrderId, [], e);
                      }}
                      style={{
                        padding: '0',
                        fontSize: vars.typography.fontSize.sm,
                      }}
                    >
                      {tkInfo.vendorOrderId ?? tkInfo.purchaseOrderId}
                    </Button>
                    <CopyToClipboard
                      textToCopy={
                        tkInfo.vendorOrderId ?? tkInfo.purchaseOrderId
                      }
                      copyIconColor={vars.color.textBrand}
                    />
                  </Stack>
                </SimpleTable.Td>
                <SimpleTable.Td className={tableStyles.tableCell}>
                  {getBuyerName(tkInfo.buyerUserId)}
                </SimpleTable.Td>
                <SimpleTable.Td className={tableStyles.tableCell}>
                  <Stack direction="row" gap="s" alignItems="center">
                    <Stack direction="column">
                      <div title={tkInfo.vendor?.vendorName}>
                        {tkInfo.vendor?.vendorName}
                      </div>
                      <div
                        title={tkInfo.vendorAccount?.encryptedEmailAddress}
                        className={styles.vendorAccountEmail}
                      >
                        ({tkInfo.vendorAccount?.encryptedEmailAddress})
                      </div>
                    </Stack>
                    {hasSurfaceBadVendAccCredentials &&
                      tkInfo.vendorAccount && (
                        <VendorAccountHealth
                          mode="compact"
                          vendorAccount={tkInfo.vendorAccount}
                        />
                      )}
                  </Stack>
                </SimpleTable.Td>
                <SimpleTable.Td
                  className={clsx(tableStyles.tableCell, tableStyles.smallCell)}
                >
                  {tkInfo.internalNotes ? (
                    <Tooltip
                      triggerContent={
                        <div>
                          <GenericDisclosuresIcon
                            fill={vars.color.textStrong}
                          />
                        </div>
                      }
                    >
                      <div className={styles.notesTooltip}>
                        <div className={styles.notesTooltipHeader}>
                          <Content id={ContentId.Notes} />
                        </div>
                        <div className={styles.notesTooltipContent}>
                          {tkInfo.internalNotes}
                        </div>
                      </div>
                    </Tooltip>
                  ) : undefined}
                </SimpleTable.Td>
              </SimpleTable.Tr>
            );
          })}
        </SimpleTable.Tbody>
      </SimpleTable.Table>
    </div>
  );
};
